import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import { api } from "@/lib/api";
import { CELLS } from "@/constants/testIds";
import { TrendingUp, LineChart as LineIcon, BarChart3 } from "lucide-react";

// Distinct, navy/gold palette + accent colors.
const PALETTE = [
  "#1c2a3a", // navy
  "#c8a04a", // gold
  "#2f6f4a", // green
  "#9b2c2c", // red
  "#5b3f8a", // purple
  "#0e7490", // teal
  "#b45309", // amber
  "#475569", // slate
  "#7a5b0f", // dark gold
  "#155e75", // dark teal
];

const TAB_LINE = "line";
const TAB_BAR = "bar";

export default function CellGrowthChart() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [tab, setTab] = useState(TAB_LINE);
  const [data, setData] = useState({ counties: [], months: [], totals_by_county: {}, totals_by_county_avg: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hiddenCounties, setHiddenCounties] = useState({});

  const load = async (yr) => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/cells/captures/growth", { params: { year: yr } });
      setData(res.data || { counties: [], months: [] });
    } catch (e) {
      setError("No se pudo cargar el crecimiento.");
      setData({ counties: [], months: [], totals_by_county: {}, totals_by_county_avg: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(year);
  }, [year]);

  const counties = data.counties || [];
  const months = data.months || [];

  const hasData = useMemo(
    () => months.some((m) => (m._total || 0) > 0),
    [months]
  );

  // Bar chart data: rows by county
  const barData = useMemo(
    () =>
      counties.map((c) => ({
        county: c,
        total: data.totals_by_county?.[c] || 0,
        avg: data.totals_by_county_avg?.[c] || 0,
      })),
    [counties, data]
  );

  const visibleCounties = counties.filter((c) => !hiddenCounties[c]);

  const yearOptions = [];
  for (let y = currentYear + 1; y >= currentYear - 4; y--) yearOptions.push(y);

  return (
    <div
      data-testid={CELLS.growthSection}
      className="mx-8 mb-8 border border-[#d9cfb8] bg-white shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#d9cfb8] bg-[#fbf7ee] px-4 py-3">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-[#7a5b0f]" />
          <h3 className="text-[12px] uppercase tracking-widest text-[#1c1a14]">
            Crecimiento de Asistencia por Condado
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded border border-[#d9cfb8]">
            <button
              data-testid={CELLS.growthTabLine}
              onClick={() => setTab(TAB_LINE)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 text-[11px] uppercase tracking-widest ${
                tab === TAB_LINE
                  ? "bg-[#1c2a3a] text-white"
                  : "bg-white text-[#5b5240] hover:bg-[#fbf7ee]"
              }`}
            >
              <LineIcon size={12} /> Evolución
            </button>
            <button
              data-testid={CELLS.growthTabBar}
              onClick={() => setTab(TAB_BAR)}
              className={`inline-flex items-center gap-1 px-3 py-1.5 text-[11px] uppercase tracking-widest border-l border-[#d9cfb8] ${
                tab === TAB_BAR
                  ? "bg-[#1c2a3a] text-white"
                  : "bg-white text-[#5b5240] hover:bg-[#fbf7ee]"
              }`}
            >
              <BarChart3 size={12} /> Comparativo
            </button>
          </div>
          <label className="text-[11px] uppercase tracking-widest text-[#5b5240]">
            Año
          </label>
          <select
            data-testid={CELLS.growthYearSelect}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded border border-[#d9cfb8] bg-white px-2 py-1 text-[12px] text-[#1c1a14]"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="p-4">
        {error && (
          <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            {error}
          </div>
        )}
        {loading ? (
          <div className="py-16 text-center text-[12px] text-[#8b7e5e]">Cargando crecimiento…</div>
        ) : counties.length === 0 || !hasData ? (
          <div
            data-testid={CELLS.growthEmpty}
            className="py-16 text-center text-[12px] text-[#8b7e5e]"
          >
            Aún no hay capturas de asistencia para {year}.
            <div className="mt-1 text-[11px] text-[#a0926b]">
              Asegúrate de asignar un <strong>Condado</strong> a cada célula y registrar capturas semanales.
            </div>
          </div>
        ) : (
          <>
            <div
              data-testid={CELLS.growthCountyFilter}
              className="mb-3 flex flex-wrap gap-2"
            >
              {counties.map((c, idx) => {
                const color = PALETTE[idx % PALETTE.length];
                const hidden = !!hiddenCounties[c];
                return (
                  <button
                    key={c}
                    onClick={() =>
                      setHiddenCounties((h) => ({ ...h, [c]: !h[c] }))
                    }
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] tracking-wide transition ${
                      hidden
                        ? "border-[#d9cfb8] bg-white text-[#a0926b] line-through"
                        : "border-[#1c2a3a] bg-[#1c2a3a]/5 text-[#1c1a14]"
                    }`}
                    title={hidden ? "Mostrar" : "Ocultar"}
                  >
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: color, opacity: hidden ? 0.3 : 1 }}
                    />
                    {c}
                    <span className="text-[10px] text-[#7a5b0f]">
                      {data.totals_by_county?.[c] || 0}
                    </span>
                  </button>
                );
              })}
            </div>

            <div
              data-testid={CELLS.growthChart}
              className="h-[340px] w-full"
            >
              {tab === TAB_LINE ? (
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 260 }}>
                  <LineChart data={months} margin={{ top: 10, right: 24, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8e1cd" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "#5b5240" }}
                      axisLine={{ stroke: "#d9cfb8" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#5b5240" }}
                      axisLine={{ stroke: "#d9cfb8" }}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#fffdf5",
                        border: "1px solid #d9cfb8",
                        borderRadius: 4,
                        fontSize: 12,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {visibleCounties.map((c, idx) => {
                      const realIdx = counties.indexOf(c);
                      const color = PALETTE[realIdx % PALETTE.length];
                      return (
                        <Line
                          key={c}
                          type="monotone"
                          dataKey={c}
                          stroke={color}
                          strokeWidth={2}
                          dot={{ r: 3, strokeWidth: 0, fill: color }}
                          activeDot={{ r: 5 }}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 260 }}>
                  <BarChart
                    data={barData.filter((b) => !hiddenCounties[b.county])}
                    margin={{ top: 10, right: 24, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e8e1cd" />
                    <XAxis
                      dataKey="county"
                      tick={{ fontSize: 11, fill: "#5b5240" }}
                      axisLine={{ stroke: "#d9cfb8" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#5b5240" }}
                      axisLine={{ stroke: "#d9cfb8" }}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#fffdf5",
                        border: "1px solid #d9cfb8",
                        borderRadius: 4,
                        fontSize: 12,
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="total" name="Asistencia total" fill="#1c2a3a" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="avg" name="Promedio por sesión" fill="#c8a04a" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
